<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const actionTypes = [
  {
    action: 'Dibuat',
    color: 'success',
    icon: 'ri-add-circle-line',
    contoh: 'Produk baru ditambahkan, supplier baru didaftarkan',
  },
  {
    action: 'Diubah',
    color: 'warning',
    icon: 'ri-edit-line',
    contoh: 'Harga produk diubah, stok minimum bahan baku diupdate',
  },
  {
    action: 'Dihapus',
    color: 'error',
    icon: 'ri-delete-bin-line',
    contoh: 'Kategori dihapus, supplier dihapus dari sistem',
  },
  {
    action: 'Void',
    color: 'error',
    icon: 'ri-close-circle-line',
    contoh: 'Transaksi di-void, purchase order dibatalkan',
  },
  {
    action: 'Dikonfirmasi',
    color: 'info',
    icon: 'ri-checkbox-circle-line',
    contoh: 'Stock opname dikonfirmasi, penerimaan barang disetujui',
  },
  {
    action: 'Login',
    color: 'primary',
    icon: 'ri-login-circle-line',
    contoh: 'Kasir login ke sistem',
  },
  {
    action: 'Logout',
    color: 'secondary',
    icon: 'ri-logout-circle-line',
    contoh: 'Pengguna keluar dari aplikasi',
  },
]

const filterFields = [
  {
    nama: 'Kolom Cari',
    fungsi: 'Ketik nama user atau nomor ID data yang ingin dicari',
    contoh: 'Ketik "Budi" untuk lihat semua aksi yang dilakukan Budi',
  },
  {
    nama: 'Filter Aksi',
    fungsi: 'Saring log berdasarkan jenis aksi (Dibuat, Diubah, Dihapus, dll)',
    contoh: 'Pilih "Diubah" untuk lihat semua perubahan data hari ini',
  },
  {
    nama: 'Filter Tipe Data',
    fungsi: 'Saring berdasarkan jenis data yang diubah (Produk, Transaksi, Bahan Baku, dll)',
    contoh: 'Pilih "Transaksi" untuk lihat semua void order',
  },
  {
    nama: 'Dari Tanggal',
    fungsi: 'Tampilkan log mulai tanggal tertentu',
    contoh: '01/07/2025 — log dari awal bulan Juli',
  },
  {
    nama: 'Sampai Tanggal',
    fungsi: 'Batas akhir rentang tanggal yang ditampilkan',
    contoh: '31/07/2025 — log sampai akhir bulan Juli',
  },
]

const skenarioUmum = [
  {
    skenario: 'Kasir Curiga Ada Manipulasi Harga',
    langkah: [
      'Buka Audit Log',
      'Filter Tipe Data → "Produk" atau "Varian Produk"',
      'Filter Aksi → "Diubah"',
      'Set rentang tanggal ke periode yang dicurigai',
      'Klik log yang relevan dan lihat "Nilai Lama" vs "Nilai Baru" di kolom harga',
    ],
    tips: 'Lihat kolom "Pengguna" untuk tahu siapa yang mengubah harga tersebut.',
  },
  {
    skenario: 'Stok Tiba-Tiba Berkurang Drastis',
    langkah: [
      'Buka Pergerakan Stok terlebih dahulu untuk lihat angka selisihnya',
      'Buka Audit Log',
      'Filter Tipe Data → "Bahan Baku" atau "Stock Opname"',
      'Filter Aksi → "Diubah" atau "Dikonfirmasi"',
      'Cocokkan tanggal dan jam dengan penurunan stok yang terjadi',
    ],
    tips: 'Jika penurunan stok terjadi saat tidak ada transaksi, cek apakah ada Stock Adjustment.',
  },
  {
    skenario: 'Ingin Tahu Siapa yang Login Hari Ini',
    langkah: [
      'Buka Audit Log',
      'Set Dari Tanggal dan Sampai Tanggal ke hari ini',
      'Filter Aksi → "Login"',
      'Daftar semua pengguna yang login akan muncul beserta jamnya',
    ],
    tips: 'Gunakan ini jika Anda ingin verifikasi siapa saja yang masuk ke toko pada hari tertentu.',
  },
  {
    skenario: 'Ada Transaksi yang Di-void Tanpa Sepengetahuan Anda',
    langkah: [
      'Buka Audit Log',
      'Filter Tipe Data → "Transaksi"',
      'Filter Aksi → "Void"',
      'Klik log void → lihat detail siapa yang void dan jam berapa',
    ],
    tips: 'Anda juga bisa cek laporan penjualan dan cocokkan dengan laporan void di modul order.',
  },
]

const faq = [
  {
    q: 'Apakah Audit Log bisa dihapus atau diedit?',
    a: 'Tidak. Audit Log bersifat permanen dan tidak bisa diubah oleh siapapun, termasuk pemilik toko. Ini sengaja dibuat seperti itu agar log tidak bisa dimanipulasi. Ini seperti CCTV yang rekaman-nya tidak bisa diedit.',
  },
  {
    q: 'Berapa lama log disimpan?',
    a: 'Log disimpan selama akun toko Anda aktif. Tidak ada penghapusan otomatis berdasarkan waktu. Namun untuk performa terbaik, gunakan filter tanggal saat mencari log agar halaman tidak lambat.',
  },
  {
    q: 'Apakah kasir biasa bisa melihat Audit Log?',
    a: 'Tidak. Audit Log hanya bisa diakses oleh pengguna dengan role Owner atau Manager. Kasir tidak akan melihat menu ini di sidebar mereka.',
  },
  {
    q: 'Apa arti "Nilai Lama" dan "Nilai Baru" di detail log?',
    a: 'Nilai Lama adalah data sebelum diubah. Nilai Baru adalah data setelah diubah. Contoh: harga produk "Es Kopi Susu" diubah dari Rp 18.000 (Nilai Lama) menjadi Rp 22.000 (Nilai Baru). Jika kolom Nilai Lama kosong, berarti data baru dibuat. Jika Nilai Baru kosong, berarti data dihapus.',
  },
  {
    q: 'IP Address itu apa? Apakah penting?',
    a: 'IP Address adalah "alamat" perangkat yang digunakan untuk login atau melakukan aksi. Jika Anda melihat IP Address yang tidak dikenal melakukan aksi penting, ini bisa jadi tanda ada akses tidak sah. Segera ganti password pengguna yang bersangkutan.',
  },
  {
    q: 'Kenapa ada log dengan pengguna "System"?',
    a: 'Beberapa aksi dilakukan otomatis oleh sistem, bukan oleh manusia. Contoh: sistem otomatis kirim notifikasi stok menipis setiap jam 08.00. Aksi seperti ini akan tercatat dengan pengguna "System".',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink
            to="/userguide/sistem"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            Sistem
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Audit Log</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow class="mb-2">
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="error" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-shield-check-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Audit Log — Rekam Jejak Aktivitas</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Pantau semua perubahan data di toko Anda: siapa yang melakukan, kapan, dan apa yang diubah
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line" class="mb-0">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda memahami dan menggunakan fitur Audit Log untuk memantau
          aktivitas seluruh pengguna di toko Anda — mulai dari perubahan harga produk,
          penghapusan data, hingga siapa yang login hari ini.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="error" />
            Apa itu Audit Log dan Mengapa Penting?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Bayangkan toko Anda punya <strong>CCTV digital</strong> yang merekam
              setiap kali seseorang mengubah data di sistem — bukan gambar, tapi
              catatan tertulis: <em>"Siapa yang mengubah apa, jam berapa, dari nilai
              berapa menjadi berapa."</em>
            </p>

            <p class="text-body-2 mb-3">
              Itulah yang disebut <strong>Audit Log</strong>. Setiap kali ada pengguna
              yang menambah, mengubah, atau menghapus data di PintuKasir — misalnya
              mengubah harga produk, menghapus bahan baku, atau melakukan void transaksi
              — sistem otomatis mencatat kejadian tersebut. Anda tidak perlu mengaktifkan
              apapun; fitur ini berjalan diam-diam di belakang layar setiap saat.
            </p>

            <VAlert type="success" variant="tonal" class="mb-0">
              <div class="d-flex gap-2 align-start">
                <VIcon icon="ri-lightbulb-flash-line" size="18" class="mt-1 flex-shrink-0" />
                <div>
                  <strong>Analogi sederhana:</strong> Seperti buku mutasi rekening bank.
                  Anda bisa lihat kapan uang masuk, kapan keluar, siapa yang transfer,
                  berapa jumlahnya — dan tidak ada satu baris pun yang bisa dihapus.
                  Audit Log bekerja dengan cara yang sama untuk semua data di toko Anda.
                </div>
              </div>
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Siapa yang bisa akses -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-user-settings-line" class="me-2" color="warning" />
            Siapa yang Bisa Melihat Audit Log?
          </VCardTitle>
          <VCardText>
            <VRow>
              <VCol cols="12" md="4">
                <VCard variant="tonal" color="success" class="h-100">
                  <VCardText class="text-center py-5">
                    <VIcon icon="ri-vip-crown-line" size="36" class="mb-2" />
                    <div class="text-subtitle-1 font-weight-bold mb-1">Owner</div>
                    <VChip color="success" size="small" variant="elevated">✅ Bisa Akses</VChip>
                    <p class="text-body-2 mt-3 mb-0">
                      Punya akses penuh ke seluruh Audit Log semua pengguna
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard variant="tonal" color="warning" class="h-100">
                  <VCardText class="text-center py-5">
                    <VIcon icon="ri-user-star-line" size="36" class="mb-2" />
                    <div class="text-subtitle-1 font-weight-bold mb-1">Manager</div>
                    <VChip color="warning" size="small" variant="elevated">✅ Bisa Akses</VChip>
                    <p class="text-body-2 mt-3 mb-0">
                      Bisa lihat log aktivitas di toko yang ia kelola
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard variant="tonal" color="error" class="h-100">
                  <VCardText class="text-center py-5">
                    <VIcon icon="ri-user-line" size="36" class="mb-2" />
                    <div class="text-subtitle-1 font-weight-bold mb-1">Kasir</div>
                    <VChip color="error" size="small" variant="elevated">❌ Tidak Bisa</VChip>
                    <p class="text-body-2 mt-3 mb-0">
                      Menu Audit Log tidak muncul di tampilan kasir
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Buka & Gunakan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-play-circle-line" class="me-2" color="success" />
            Cara Membuka dan Menggunakan Audit Log
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-4">
                Buka menu <strong>Sistem → Audit Log</strong> dari sidebar kiri.
                <br>
                <span class="text-medium-emphasis">
                  (Jika menu ini tidak muncul, Anda tidak punya izin akses — hubungi Owner toko)
                </span>
              </li>

              <li class="mb-4">
                Anda akan melihat <strong>tabel log aktivitas</strong> yang menampilkan kolom:
                <VTable density="compact" class="mt-2 mb-1">
                  <thead>
                    <tr>
                      <th>Kolom</th>
                      <th>Artinya</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Waktu</strong></td>
                      <td class="text-body-2">Tanggal dan jam aksi dilakukan (format: 01 Jul 2025, 14:35)</td>
                    </tr>
                    <tr>
                      <td><strong>Pengguna</strong></td>
                      <td class="text-body-2">Nama + email orang yang melakukan aksi. "System" = otomatis oleh sistem</td>
                    </tr>
                    <tr>
                      <td><strong>Aksi</strong></td>
                      <td class="text-body-2">Jenis perubahan: Dibuat / Diubah / Dihapus / Void / Login / dll</td>
                    </tr>
                    <tr>
                      <td><strong>Tipe Data</strong></td>
                      <td class="text-body-2">Data apa yang diubah: Produk / Transaksi / Bahan Baku / Shift / dll</td>
                    </tr>
                    <tr>
                      <td><strong>ID</strong></td>
                      <td class="text-body-2">Nomor urut unik data yang diubah di sistem</td>
                    </tr>
                    <tr>
                      <td><strong>IP Address</strong></td>
                      <td class="text-body-2">Alamat jaringan perangkat yang digunakan saat melakukan aksi</td>
                    </tr>
                  </tbody>
                </VTable>
              </li>

              <li class="mb-4">
                Gunakan <strong>filter di bagian atas tabel</strong> untuk mempersempit pencarian.
                Penjelasan setiap filter:
                <VTable density="compact" class="mt-2 mb-1">
                  <thead>
                    <tr>
                      <th style="min-width: 140px">Filter</th>
                      <th>Fungsi & Contoh Penggunaan</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="f in filterFields" :key="f.nama">
                      <td><strong>{{ f.nama }}</strong></td>
                      <td class="text-body-2 py-2">
                        {{ f.fungsi }}<br>
                        <span class="text-medium-emphasis text-caption">Contoh: {{ f.contoh }}</span>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>

              <li class="mb-4">
                Klik tombol <strong>ikon mata (👁)</strong> di ujung kanan setiap baris
                untuk melihat <strong>detail perubahan</strong>.
              </li>

              <li class="mb-0">
                Di dialog detail, Anda akan melihat dua kolom:
                <VRow class="mt-2">
                  <VCol cols="12" md="6">
                    <VCard variant="outlined" color="error" class="pa-3">
                      <div class="d-flex align-center gap-2 mb-2">
                        <VIcon icon="ri-arrow-left-line" color="error" size="16" />
                        <span class="text-subtitle-2 font-weight-bold text-error">Nilai Lama</span>
                      </div>
                      <p class="text-body-2 mb-1">Data <strong>sebelum</strong> diubah.</p>
                      <p class="text-caption text-medium-emphasis mb-0">
                        Contoh: harga = 18000
                      </p>
                    </VCard>
                  </VCol>
                  <VCol cols="12" md="6">
                    <VCard variant="outlined" color="success" class="pa-3">
                      <div class="d-flex align-center gap-2 mb-2">
                        <VIcon icon="ri-arrow-right-line" color="success" size="16" />
                        <span class="text-subtitle-2 font-weight-bold text-success">Nilai Baru</span>
                      </div>
                      <p class="text-body-2 mb-1">Data <strong>setelah</strong> diubah.</p>
                      <p class="text-caption text-medium-emphasis mb-0">
                        Contoh: harga = 22000
                      </p>
                    </VCard>
                  </VCol>
                </VRow>
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Jenis Aksi -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-bookmark-3-line" class="me-2" color="info" />
            Jenis-Jenis Aksi yang Dicatat
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Setiap aksi ditandai dengan chip berwarna agar mudah dibedakan sekilas pandang.
            </p>
            <VRow>
              <VCol
                v-for="item in actionTypes"
                :key="item.action"
                cols="12"
                sm="6"
                md="4"
              >
                <VCard variant="outlined" class="pa-3 h-100">
                  <div class="d-flex align-center gap-2 mb-2">
                    <VChip :color="item.color" variant="tonal" size="small" :prepend-icon="item.icon" label>
                      {{ item.action }}
                    </VChip>
                  </div>
                  <p class="text-caption text-medium-emphasis mb-0">{{ item.contoh }}</p>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Skenario Penggunaan Nyata -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-guide-line" class="me-2" color="primary" />
            Skenario Penggunaan Nyata
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Berikut situasi nyata yang sering dihadapi pemilik toko,
              dan cara menggunakan Audit Log untuk investigasinya.
            </p>

            <VExpansionPanels variant="accordion">
              <VExpansionPanel v-for="(item, i) in skenarioUmum" :key="i">
                <VExpansionPanelTitle class="text-body-2 font-weight-medium">
                  🔍 {{ item.skenario }}
                </VExpansionPanelTitle>
                <VExpansionPanelText>
                  <p class="text-body-2 font-weight-medium mb-2">Langkah-langkah:</p>
                  <ol class="text-body-2 ps-4 mb-3">
                    <li v-for="(l, li) in item.langkah" :key="li" class="mb-1">{{ l }}</li>
                  </ol>
                  <VAlert type="info" variant="tonal" density="compact">
                    💡 {{ item.tips }}
                  </VAlert>
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Peringatan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-error-warning-line" class="me-2" />
            ⚠️ Hal Penting yang Perlu Diketahui
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-close-circle-line" class="mb-2">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Audit Log tidak bisa dihapus atau diedit.</strong>
                  Ini fitur keamanan — bukan bug. Rekaman aktivitas harus terjaga keasliannya.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-eye-off-line" class="mb-2">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Beberapa kolom sensitif tidak dicatat.</strong>
                  Password, token akses, dan data sensitif lainnya tidak pernah muncul
                  di Audit Log, meski data tersebut diubah. Ini untuk keamanan akun Anda.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-time-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Log bisa banyak — gunakan filter.</strong>
                  Toko yang aktif bisa menghasilkan ratusan log per hari.
                  Selalu gunakan filter tanggal dan jenis aksi agar pencarian lebih cepat
                  dan tidak memberatkan halaman.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="success" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Penggunaan Audit Log untuk Pemilik Toko
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-calendar-check-line" class="mb-2">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Rutinkan cek mingguan.</strong>
                  Luangkan 5 menit setiap Senin untuk review log minggu lalu.
                  Filter Aksi → "Dihapus" dan "Void" untuk lihat hal-hal yang mungkin
                  perlu perhatian Anda.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-user-search-line" class="mb-2">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Cek log karyawan baru.</strong>
                  Saat ada karyawan baru, pantau aktivitasnya di Audit Log selama
                  2 minggu pertama. Filter nama mereka di kolom pencarian.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-screenshot-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Screenshot detail log sebagai bukti.</strong>
                  Jika ada indikasi kecurangan, buka detail log dan screenshot
                  sebagai dokumentasi sebelum melakukan tindakan apapun.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6 mb-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Halaman Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'User & Role', sub: 'Atur siapa yang bisa akses apa', icon: 'ri-group-line', color: 'primary', to: '/userguide/sistem/user-role' },
              { title: 'Pergerakan Stok', sub: 'Riwayat keluar masuk stok bahan baku', icon: 'ri-swap-line', color: 'info', to: '/userguide/produk-inventory/pergerakan-stok' },
              { title: 'Pengaturan', sub: 'Konfigurasi umum toko dan akun', icon: 'ri-settings-3-line', color: 'success', to: '/userguide/sistem/pengaturan' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
